/**
 * Recursively strips NUL (U+0000) characters from every string in a value,
 * returning a structurally-identical copy. Non-plain values (Date, class
 * instances, numbers, etc.) are returned untouched.
 *
 * Postgres raises `22P05` ("unsupported Unicode escape sequence") when the
 * serialized JSON text contains a `\u0000` escape, and also rejects NUL in
 * `text`/`varchar` columns. LLM providers occasionally emit NUL bytes in
 * assistant text, tool-call arguments, and thinking blocks (e.g. tokenizer
 * bugs), so any value that flows into Postgres must be sanitized first. A NUL
 * byte carries no meaning for any consumer, so dropping it is strictly better
 * than failing the write.
 *
 * NOTE: Lone surrogate halves (unpaired U+D800–U+DFFF) are a sibling failure
 * mode — they serialize to `\uD800`-style escapes that Postgres also rejects.
 * Once our TS lib target includes ES2024, prefer normalizing them here via
 * `String.prototype.toWellFormed()` (e.g. `value.toWellFormed().replace(...)`).
 */
export const stripNullBytesDeep = <T>(value: T): T => {
  if (typeof value === "string") {
    // biome-ignore lint/suspicious/noControlCharactersInRegex: stripping NUL is the point
    return value.replace(/\u0000/g, "") as T;
  }

  if (Array.isArray(value)) {
    return value.map(stripNullBytesDeep) as T;
  }

  // Only recurse into plain objects (including `Object.create(null)`) — leave
  // class instances (Date, Buffer, etc.) untouched so we don't mangle non-JSON
  // values. A prototype check is more robust than `constructor === Object`,
  // which misses null-prototype objects.
  if (value !== null && typeof value === "object") {
    const proto = Object.getPrototypeOf(value);
    if (proto === Object.prototype || proto === null) {
      return Object.fromEntries(
        Object.entries(value as Record<string, unknown>).map(([k, v]) => [
          k,
          stripNullBytesDeep(v),
        ]),
      ) as T;
    }
  }

  return value;
};

const isLineBreakingControl = (code: number) =>
  code < 0x20 || code === 0x7f || code === 0x2028 || code === 0x2029;

/** True when the text holds a C0 control, DEL, or a Unicode line or paragraph separator. */
export function hasControlCharacters(text: string): boolean {
  return [...text].some((char) => isLineBreakingControl(char.charCodeAt(0)));
}

/** Escapes control characters as \uXXXX, so a value cannot break out of the prompt line it is rendered into. */
export function escapeControlCharacters(text: string): string {
  return [...text]
    .map((char) => {
      const code = char.charCodeAt(0);
      return isLineBreakingControl(code)
        ? `\\u${code.toString(16).padStart(4, "0")}`
        : char;
    })
    .join("");
}
